import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterContext, type DateRange, type Granularity, rangeToDates } from './filters';
import { FilterBar } from './FilterBar';
import { NAV, type MeasurementPage } from './shell-types';
import { OverviewPage } from './pages/OverviewPage';
import { PromptsPage } from './pages/PromptsPage';
import { SourcesDomainsPage } from './pages/SourcesDomainsPage';
import { SourcesUrlsPage } from './pages/SourcesUrlsPage';
import { SourcesGapPage } from './pages/SourcesGapPage';
import { BrandsPage } from './pages/BrandsPage';
import { ClustersPage } from './pages/ClustersPage';
import { EnginesPage } from './pages/EnginesPage';
import { ActionsPage, useActionsCount } from './pages/ActionsPage';
import { ChatsPage } from './pages/ChatsPage';
import { ProfilePage } from './pages/ProfilePage';
import { AddProbeModal } from './pages/shared/AddProbeModal';

const VALID_PAGES = new Set<MeasurementPage>([
  'overview', 'prompts', 'sources-domains', 'sources-urls', 'sources-gap',
  'brands', 'clusters', 'engines', 'actions', 'chats', 'profile',
]);

export function MeasurementShell() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawPage = searchParams.get('page');
  const page: MeasurementPage = VALID_PAGES.has(rawPage as MeasurementPage)
    ? (rawPage as MeasurementPage)
    : 'overview';

  const goto = (p: MeasurementPage) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (p === 'overview') next.delete('page');
      else next.set('page', p);
      return next;
    });
  };

  const [range, setRange] = useState<DateRange>('30d');
  const [granularity, setGranularity] = useState<Granularity>('D');
  const [modelId, setModelId] = useState<string | null>(null);
  const [topicId, setTopicId] = useState<string | null>(null);
  const [tagId, setTagId] = useState<string | null>(null);
  const [addPromptOpen, setAddPromptOpen] = useState(false);

  const { startDate, endDate } = useMemo(() => rangeToDates(range), [range]);

  const ctx = {
    range, granularity, modelId, topicId, tagId,
    startDate, endDate,
    setRange, setGranularity, setModelId, setTopicId, setTagId,
  };

  return (
    <FilterContext.Provider value={ctx}>
      <div className="grid gap-6 lg:grid-cols-[200px_1fr] items-start">
        <Sidebar page={page} onGoto={goto} />
        <div className="min-w-0 space-y-5">
          <FilterBar />
          <PageBody page={page} onGoto={goto} onAddProbe={() => setAddPromptOpen(true)} />
        </div>
      </div>
      {addPromptOpen && <AddProbeModal onClose={() => setAddPromptOpen(false)} />}
    </FilterContext.Provider>
  );
}

function Sidebar({ page, onGoto }: { page: MeasurementPage; onGoto: (p: MeasurementPage) => void }) {
  const groups = NAV.reduce((acc, item) => {
    (acc[item.group] ??= []).push(item);
    return acc;
  }, {} as Record<string, typeof NAV>);
  const actionsCount = useActionsCount();

  return (
    <nav className="lg:sticky lg:top-4 self-start space-y-5">
      {Object.entries(groups).map(([group, items]) => (
        <div key={group}>
          <p className="text-body-xs uppercase tracking-wider text-text-tertiary px-2 mb-1.5">{group}</p>
          <ul className="space-y-0.5">
            {items.map((item) => {
              const isActive = item.id === page || (item.id === 'overview' && page === 'overview');
              const badge = item.id === 'actions' && actionsCount > 0 ? actionsCount : null;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onGoto(item.id)}
                    className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-body-sm transition-colors ${
                      isActive
                        ? 'bg-surface-secondary font-medium text-text-primary'
                        : 'text-text-secondary hover:bg-surface-secondary/60 hover:text-text-primary'
                    }`}
                  >
                    <span>{item.label}</span>
                    {badge != null && (
                      <span className="inline-flex items-center justify-center rounded-full bg-text-primary text-text-inverse text-[10px] font-semibold h-4 min-w-[16px] px-1">
                        {badge}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function PageBody({ page, onGoto, onAddProbe }: { page: MeasurementPage; onGoto: (p: MeasurementPage) => void; onAddProbe: () => void }) {
  switch (page) {
    case 'overview': return <OverviewPage onGoto={onGoto} />;
    case 'prompts': return <PromptsPage onAddProbe={onAddProbe} />;
    case 'sources-domains': return <SourcesDomainsPage />;
    case 'sources-urls': return <SourcesUrlsPage />;
    case 'sources-gap': return <SourcesGapPage />;
    case 'brands': return <BrandsPage />;
    case 'clusters': return <ClustersPage />;
    case 'engines': return <EnginesPage onGoto={onGoto} />;
    case 'actions': return <ActionsPage />;
    case 'chats': return <ChatsPage />;
    case 'profile': return <ProfilePage />;
  }
}
